/**
 * VacationRequestForm - Multi-step form for creating vacation requests
 * Implements 3-step wizard: Details → Review → Confirmation
 * Story: VAC-DS-04
 */

import React, { useState, useEffect, useCallback } from 'react'
import {
  CheckCircle,
  AlertCircle,
  Copy,
  Edit,
  Info,
  ChevronRight
} from 'lucide-react'
import type {
  VacationBalance,
  VacationRequest,
  VacationRequestType,
  VacationRequestDraft,
  CreateVacationRequestInput
} from '@/types/vacation'
import {
  calculateBusinessDays,
  formatDateBR,
  formatDateRange,
  shouldDisableDate,
  toISODateString,
  getMinimumSelectableDate
} from '@/lib/utils/date-utils'
import {
  validateVacationRequest,
  validateField,
  getVacationTypeLabel,
  getVacationTypeDescription
} from '@/lib/utils/clt-validator'
import { VacationRequestType as VacationTypeEnum } from '@/types/vacation'

interface VacationRequestFormProps {
  balance: VacationBalance
  onSuccess: (request: VacationRequest) => void
  onError: (error: Error) => void
  onSubmit: (input: CreateVacationRequestInput) => Promise<VacationRequest>
}

const DRAFT_STORAGE_KEY = 'vacation-request-draft'
const DRAFT_SAVE_INTERVAL = 30000 // 30 seconds

const steps = ['Detalhes', 'Revisar', 'Confirmação']

export const VacationRequestForm: React.FC<VacationRequestFormProps> = ({
  balance,
  onSuccess,
  onError,
  onSubmit
}) => {
  const [activeStep, setActiveStep] = useState(0)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [createdRequest, setCreatedRequest] = useState<VacationRequest | null>(null)

  // Form state
  const [startDate, setStartDate] = useState<Date | null>(null)
  const [endDate, setEndDate] = useState<Date | null>(null)
  const [vacationType, setVacationType] = useState<VacationRequestType | null>(null)
  const [notes, setNotes] = useState('')

  // Validation errors
  const [errors, setErrors] = useState<Record<string, string>>({})

  // Calculate business days
  const businessDays = startDate && endDate ? calculateBusinessDays(startDate, endDate) : 0
  const balanceAfterApproval = balance.daysAvailable - businessDays

  // Save draft to localStorage
  const saveDraft = useCallback(() => {
    const draft: VacationRequestDraft = {
      startDate: startDate ? toISODateString(startDate) : null,
      endDate: endDate ? toISODateString(endDate) : null,
      type: vacationType,
      notes,
      savedAt: new Date().toISOString()
    }
    localStorage.setItem(DRAFT_STORAGE_KEY, JSON.stringify(draft))
  }, [startDate, endDate, vacationType, notes])

  // Load draft from localStorage on mount
  useEffect(() => {
    const savedDraft = localStorage.getItem(DRAFT_STORAGE_KEY)
    if (savedDraft) {
      try {
        const draft: VacationRequestDraft = JSON.parse(savedDraft)
        if (draft.startDate) setStartDate(new Date(draft.startDate))
        if (draft.endDate) setEndDate(new Date(draft.endDate))
        if (draft.type) setVacationType(draft.type)
        if (draft.notes) setNotes(draft.notes)
      } catch (err) {
        console.error('Failed to load draft:', err)
      }
    }
  }, [])

  // Auto-save draft every 30 seconds
  useEffect(() => {
    const interval = setInterval(saveDraft, DRAFT_SAVE_INTERVAL)
    return () => clearInterval(interval)
  }, [saveDraft])

  // Validate field on blur
  const handleFieldBlur = (field: string) => {
    const newErrors = { ...errors }

    switch (field) {
      case 'startDate':
        const startError = validateField.startDate(startDate)
        if (startError) {
          newErrors.startDate = startError
        } else {
          delete newErrors.startDate
        }
        break

      case 'endDate':
        const endError = validateField.endDate(startDate, endDate)
        if (endError) {
          newErrors.endDate = endError
        } else {
          delete newErrors.endDate
        }
        break

      case 'type':
        const typeError = validateField.type(vacationType, startDate, endDate)
        if (typeError) {
          newErrors.type = typeError
        } else {
          delete newErrors.type
        }
        break

      case 'notes':
        const notesError = validateField.notes(notes)
        if (notesError) {
          newErrors.notes = notesError
        } else {
          delete newErrors.notes
        }
        break
    }

    setErrors(newErrors)
  }

  // Validate entire form
  const validateForm = (): boolean => {
    if (!startDate || !endDate || !vacationType) {
      return false
    }

    const validation = validateVacationRequest(
      startDate,
      endDate,
      vacationType,
      balance.daysAvailable
    )

    if (!validation.valid) {
      const newErrors: Record<string, string> = {}
      validation.errors.forEach((error, index) => {
        newErrors[`form-${index}`] = error
      })
      setErrors(newErrors)
      return false
    }

    return true
  }

  // Handle next step
  const handleNext = () => {
    if (activeStep === 0) {
      if (!validateForm()) {
        return
      }
    }

    setActiveStep((prev) => prev + 1)
  }

  // Handle back step
  const handleBack = () => {
    setActiveStep((prev) => prev - 1)
  }

  // Handle form submission
  const handleSubmit = async () => {
    if (!startDate || !endDate || !vacationType) return

    setIsSubmitting(true)
    setErrors({})

    try {
      const input: CreateVacationRequestInput = {
        startDate: toISODateString(startDate),
        endDate: toISODateString(endDate),
        type: vacationType,
        notes: notes.trim() || undefined
      }

      const request = await onSubmit(input)
      setCreatedRequest(request)

      // Clear draft on success
      localStorage.removeItem(DRAFT_STORAGE_KEY)

      // Move to confirmation step
      setActiveStep(2)
      onSuccess(request)
    } catch (err) {
      onError(err as Error)
      setErrors({ submit: (err as Error).message })
    } finally {
      setIsSubmitting(false)
    }
  }

  // Copy request ID to clipboard
  const handleCopyRequestId = () => {
    if (createdRequest?.requestId) {
      navigator.clipboard.writeText(createdRequest.requestId)
    }
  }

  // Render Step 1: Request Details
  const renderDetailsStep = () => (
    <div>
      {/* Balance Summary Card */}
      <div className="card mb-8">
        <h3 className="font-display font-semibold text-white mb-4">
          Seu Saldo de Férias
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-brand-dark/50 rounded-monumenta p-4">
            <p className="text-sm text-brand-gray mb-1">Dias Disponíveis</p>
            <p className="text-2xl font-display font-bold text-brand-primary">
              {balance.daysAvailable}
            </p>
          </div>
          <div className="bg-brand-dark/50 rounded-monumenta p-4">
            <p className="text-sm text-brand-gray mb-1">Período de Aquisição</p>
            <p className="text-sm font-display text-white">
              {formatDateBR(balance.acquisitionStartDate)}
            </p>
            <p className="text-xs text-brand-gray">até</p>
            <p className="text-sm font-display text-white">
              {formatDateBR(balance.acquisitionEndDate)}
            </p>
          </div>
          <div className="bg-brand-dark/50 rounded-monumenta p-4">
            <p className="text-sm text-brand-gray mb-1">Vencimento</p>
            <p className="text-sm font-display text-warning">
              {formatDateBR(balance.expirationDate)}
            </p>
          </div>
        </div>
      </div>

      {/* Vacation Type Selection */}
      <div className="mb-6">
        <label className="block text-sm font-display font-medium text-white mb-3">
          Tipo de Férias
        </label>
        <div className="space-y-3">
          {/* Full vacation option */}
          <label className={`card-hover cursor-pointer flex items-start p-4 ${
            vacationType === VacationTypeEnum.FULL ? 'border-brand-primary ring-2 ring-brand-primary/20' : ''
          }`}>
            <input
              type="radio"
              name="vacationType"
              value={VacationTypeEnum.FULL}
              checked={vacationType === VacationTypeEnum.FULL}
              onChange={(e) => setVacationType(e.target.value as VacationRequestType)}
              onBlur={() => handleFieldBlur('type')}
              className="form-radio mt-1"
            />
            <div className="ml-3 flex-1">
              <p className="font-display font-medium text-white">
                {getVacationTypeLabel(VacationTypeEnum.FULL)}
              </p>
              <p className="text-sm text-brand-gray mt-1 font-body">
                {getVacationTypeDescription(VacationTypeEnum.FULL)}
              </p>
            </div>
          </label>

          {/* Split vacation option */}
          <label className={`card-hover cursor-pointer flex items-start p-4 ${
            vacationType === VacationTypeEnum.SPLIT ? 'border-brand-primary ring-2 ring-brand-primary/20' : ''
          }`}>
            <input
              type="radio"
              name="vacationType"
              value={VacationTypeEnum.SPLIT}
              checked={vacationType === VacationTypeEnum.SPLIT}
              onChange={(e) => setVacationType(e.target.value as VacationRequestType)}
              onBlur={() => handleFieldBlur('type')}
              className="form-radio mt-1"
            />
            <div className="ml-3 flex-1">
              <p className="font-display font-medium text-white">
                {getVacationTypeLabel(VacationTypeEnum.SPLIT)}
              </p>
              <p className="text-sm text-brand-gray mt-1 font-body">
                {getVacationTypeDescription(VacationTypeEnum.SPLIT)}
              </p>
            </div>
          </label>

          {/* Sell third option */}
          <label className={`card-hover flex items-start p-4 ${
            !balance.canSellThird ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
          } ${vacationType === VacationTypeEnum.SELL_THIRD ? 'border-brand-primary ring-2 ring-brand-primary/20' : ''}`}>
            <input
              type="radio"
              name="vacationType"
              value={VacationTypeEnum.SELL_THIRD}
              checked={vacationType === VacationTypeEnum.SELL_THIRD}
              onChange={(e) => setVacationType(e.target.value as VacationRequestType)}
              onBlur={() => handleFieldBlur('type')}
              disabled={!balance.canSellThird}
              className="form-radio mt-1"
            />
            <div className="ml-3 flex-1">
              <p className="font-display font-medium text-white">
                {getVacationTypeLabel(VacationTypeEnum.SELL_THIRD)}
              </p>
              <p className="text-sm text-brand-gray mt-1 font-body">
                {getVacationTypeDescription(VacationTypeEnum.SELL_THIRD)}
              </p>
            </div>
          </label>
        </div>
        {errors.type && (
          <p className="text-sm text-error mt-2 flex items-center gap-1">
            <AlertCircle size={14} />
            {errors.type}
          </p>
        )}
      </div>

      {/* Date Range Picker */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <div className="form-group">
          <label className="form-label">
            Data de Início
          </label>
          <input
            type="date"
            value={startDate ? toISODateString(startDate) : ''}
            onChange={(e) => setStartDate(e.target.value ? new Date(e.target.value) : null)}
            onBlur={() => handleFieldBlur('startDate')}
            min={toISODateString(getMinimumSelectableDate())}
            className={errors.startDate ? 'form-input-error w-full' : 'form-input w-full'}
          />
          {errors.startDate && (
            <p className="form-error flex items-center gap-1">
              <AlertCircle size={14} />
              {errors.startDate}
            </p>
          )}
        </div>

        <div className="form-group">
          <label className="form-label">
            Data de Término
          </label>
          <input
            type="date"
            value={endDate ? toISODateString(endDate) : ''}
            onChange={(e) => setEndDate(e.target.value ? new Date(e.target.value) : null)}
            onBlur={() => handleFieldBlur('endDate')}
            min={startDate ? toISODateString(startDate) : toISODateString(getMinimumSelectableDate())}
            disabled={!startDate}
            className={errors.endDate ? 'form-input-error w-full' : 'form-input w-full'}
          />
          {errors.endDate && (
            <p className="form-error flex items-center gap-1">
              <AlertCircle size={14} />
              {errors.endDate}
            </p>
          )}
        </div>
      </div>

      {/* Business Days Display */}
      {startDate && endDate && businessDays > 0 && (
        <div className="bg-info/10 border border-info/30 rounded-monumenta p-4 mb-6">
          <div className="flex items-center space-x-3">
            <Info className="text-info flex-shrink-0" size={20} />
            <div className="flex-1">
              <p className="text-white font-body">
                <strong className="font-display">{businessDays} dias úteis</strong> selecionados
                {balanceAfterApproval >= 0 && (
                  <> (Saldo restante após aprovação: <strong>{balanceAfterApproval} dias</strong>)</>
                )}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Notes Field */}
      <div className="form-group mb-6">
        <label className="form-label">
          Observações (opcional)
        </label>
        <textarea
          rows={4}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          onBlur={() => handleFieldBlur('notes')}
          maxLength={500}
          placeholder="Observações adicionais sobre sua solicitação de férias..."
          className={errors.notes ? 'form-textarea w-full border-error' : 'form-textarea w-full'}
        />
        {errors.notes ? (
          <p className="form-error flex items-center gap-1">
            <AlertCircle size={14} />
            {errors.notes}
          </p>
        ) : (
          <p className="form-help">{notes.length}/500 caracteres</p>
        )}
      </div>

      {/* Form-level errors */}
      {Object.keys(errors).filter(k => k.startsWith('form-')).length > 0 && (
        <div className="bg-error/10 border border-error/30 rounded-monumenta p-4 mb-6">
          <div className="flex items-start space-x-3">
            <AlertCircle className="text-error flex-shrink-0 mt-0.5" size={20} />
            <div className="flex-1">
              <h4 className="font-display font-medium text-error mb-2">
                Erros de validação
              </h4>
              <ul className="space-y-1 text-sm text-brand-gray">
                {Object.keys(errors)
                  .filter(k => k.startsWith('form-'))
                  .map(k => (
                    <li key={k} className="font-body">{errors[k]}</li>
                  ))}
              </ul>
            </div>
          </div>
        </div>
      )}
    </div>
  )

  // Render Step 2: Review & Confirm
  const renderReviewStep = () => {
    if (!startDate || !endDate || !vacationType) return null

    const validation = validateVacationRequest(
      startDate,
      endDate,
      vacationType,
      balance.daysAvailable
    )

    return (
      <div>
        {/* Summary Card */}
        <div className="card mb-6">
          <h3 className="font-display font-semibold text-white mb-4 pb-4 border-b border-brand-gray/20">
            Resumo da Solicitação
          </h3>

          <div className="space-y-4">
            <div>
              <p className="text-sm text-brand-gray mb-2 font-body">Tipo de Férias</p>
              <div className="status-primary inline-block">
                {getVacationTypeLabel(vacationType)}
              </div>
            </div>

            <div>
              <p className="text-sm text-brand-gray mb-2 font-body">Período</p>
              <p className="text-white font-display">
                {formatDateRange(toISODateString(startDate), toISODateString(endDate))}
              </p>
            </div>

            <div>
              <p className="text-sm text-brand-gray mb-2 font-body">Dias Úteis</p>
              <p className="text-white font-display text-lg">
                {businessDays} dias
              </p>
            </div>

            {notes && (
              <div>
                <p className="text-sm text-brand-gray mb-2 font-body">Observações</p>
                <p className="text-white font-body text-sm bg-brand-dark/50 p-3 rounded-monumenta">
                  {notes}
                </p>
              </div>
            )}
          </div>
        </div>

        {/* CLT Rules Checklist */}
        <div className="card mb-6">
          <h3 className="font-display font-semibold text-white mb-4 pb-4 border-b border-brand-gray/20">
            Validação CLT
          </h3>

          <div className="space-y-3">
            <div className="flex items-start gap-3">
              {validation.valid ? (
                <CheckCircle className="text-success flex-shrink-0 mt-0.5" size={20} />
              ) : (
                <AlertCircle className="text-error flex-shrink-0 mt-0.5" size={20} />
              )}
              <div className="flex-1">
                <p className="font-display text-white">Antecedência mínima de 14 dias</p>
                <p className={`text-sm font-body mt-1 ${validation.valid ? 'text-success' : 'text-error'}`}>
                  {validation.valid ? 'Atende' : 'Não atende'}
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <CheckCircle className="text-success flex-shrink-0 mt-0.5" size={20} />
              <div className="flex-1">
                <p className="font-display text-white">Período selecionado válido</p>
                <p className="text-sm text-success font-body mt-1">
                  Data de término após data de início
                </p>
              </div>
            </div>

            {vacationType === VacationTypeEnum.FULL && (
              <div className="flex items-start gap-3">
                {businessDays >= 5 ? (
                  <CheckCircle className="text-success flex-shrink-0 mt-0.5" size={20} />
                ) : (
                  <AlertCircle className="text-error flex-shrink-0 mt-0.5" size={20} />
                )}
                <div className="flex-1">
                  <p className="font-display text-white">Mínimo 5 dias para férias completas</p>
                  <p className={`text-sm font-body mt-1 ${businessDays >= 5 ? 'text-success' : 'text-error'}`}>
                    {businessDays >= 5 ? 'Atende' : 'Não atende'}
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Balance After Approval */}
        <div className={`${balanceAfterApproval < 0 ? 'bg-error/10 border-error/30' : 'bg-success/10 border-success/30'} border rounded-monumenta p-4 mb-6`}>
          <div className="flex items-center space-x-3">
            {balanceAfterApproval < 0 ? (
              <AlertCircle className="text-error flex-shrink-0" size={20} />
            ) : (
              <CheckCircle className="text-success flex-shrink-0" size={20} />
            )}
            <p className={`font-body ${balanceAfterApproval < 0 ? 'text-error' : 'text-success'}`}>
              Saldo após aprovação: <strong className="font-display">{balanceAfterApproval} dias</strong>
            </p>
          </div>
        </div>

        {/* Edit Button */}
        <button
          type="button"
          onClick={handleBack}
          className="btn-ghost mb-6 inline-flex items-center gap-2"
        >
          <Edit size={16} />
          Editar Solicitação
        </button>

        {errors.submit && (
          <div className="bg-error/10 border border-error/30 rounded-monumenta p-4">
            <div className="flex items-center space-x-3">
              <AlertCircle className="text-error flex-shrink-0" size={20} />
              <p className="text-white font-body">{errors.submit}</p>
            </div>
          </div>
        )}
      </div>
    )
  }

  // Render Step 3: Success Confirmation
  const renderConfirmationStep = () => (
    <div className="text-center py-8">
      <CheckCircle className="text-success mx-auto mb-6" size={80} />

      <h2 className="text-3xl font-display font-bold text-white mb-3">
        Solicitação Enviada!
      </h2>

      <p className="text-brand-gray font-body mb-8">
        Sua solicitação de férias foi enviada com sucesso e está aguardando aprovação.
      </p>

      {createdRequest && (
        <div className="card inline-block mb-8 px-6 py-4">
          <p className="text-sm text-brand-gray mb-2 font-body">
            ID da Solicitação
          </p>
          <div className="flex items-center gap-3">
            <p className="text-xl font-display font-semibold text-brand-primary">
              {createdRequest.requestId}
            </p>
            <button
              type="button"
              onClick={handleCopyRequestId}
              className="btn-icon"
              title="Copiar ID"
            >
              <Copy size={16} />
            </button>
          </div>
        </div>
      )}

      <div className="bg-info/10 border border-info/30 rounded-monumenta p-4 text-left max-w-md mx-auto">
        <div className="flex items-start space-x-3">
          <Info className="text-info flex-shrink-0 mt-0.5" size={20} />
          <div className="flex-1">
            <p className="font-display font-medium text-white mb-3">
              Próximos Passos:
            </p>
            <ul className="space-y-2 text-sm text-brand-gray font-body">
              <li className="flex items-start gap-2">
                <ChevronRight size={16} className="flex-shrink-0 mt-0.5" />
                <span>Seu gestor será notificado da solicitação</span>
              </li>
              <li className="flex items-start gap-2">
                <ChevronRight size={16} className="flex-shrink-0 mt-0.5" />
                <span>Você receberá uma notificação quando houver atualização</span>
              </li>
              <li className="flex items-start gap-2">
                <ChevronRight size={16} className="flex-shrink-0 mt-0.5" />
                <span>Acompanhe o status em "Minhas Solicitações"</span>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  )

  return (
    <div>
      {/* Stepper */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          {steps.map((label, index) => (
            <div key={label} className="flex items-center flex-1">
              <div className="flex items-center">
                <div className={`
                  w-10 h-10 rounded-full flex items-center justify-center font-display font-semibold
                  ${activeStep > index
                    ? 'bg-success text-white'
                    : activeStep === index
                      ? 'bg-brand-primary text-brand-dark'
                      : 'bg-brand-dark-light text-brand-gray border-2 border-brand-gray/30'
                  }
                `}>
                  {activeStep > index ? <CheckCircle size={20} /> : index + 1}
                </div>
                <span className={`ml-3 font-display font-medium ${
                  activeStep === index ? 'text-brand-primary' : 'text-brand-gray'
                }`}>
                  {label}
                </span>
              </div>
              {index < steps.length - 1 && (
                <div className={`flex-1 h-0.5 mx-4 ${
                  activeStep > index ? 'bg-success' : 'bg-brand-gray/30'
                }`} />
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Step Content */}
      <div>
        {activeStep === 0 && renderDetailsStep()}
        {activeStep === 1 && renderReviewStep()}
        {activeStep === 2 && renderConfirmationStep()}
      </div>

      {/* Navigation Buttons */}
      {activeStep < 2 && (
        <div className="flex items-center justify-between mt-8 pt-6 border-t border-brand-gray/20">
          <button
            type="button"
            disabled={activeStep === 0}
            onClick={handleBack}
            className="btn-secondary"
          >
            Voltar
          </button>

          {activeStep === 1 ? (
            <button
              type="button"
              onClick={handleSubmit}
              disabled={isSubmitting}
              className="btn-primary"
            >
              {isSubmitting ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-brand-dark border-t-transparent"></div>
                  Enviando...
                </>
              ) : (
                <>
                  <CheckCircle size={16} />
                  Enviar Solicitação
                </>
              )}
            </button>
          ) : (
            <button
              type="button"
              onClick={handleNext}
              disabled={!startDate || !endDate || !vacationType || Object.keys(errors).length > 0}
              className="btn-primary"
            >
              Próximo
              <ChevronRight size={16} />
            </button>
          )}
        </div>
      )}
    </div>
  )
}
